import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Box, CircularProgress, Alert, Button } from '@mui/material';
import ComputerIcon from '@mui/icons-material/Computer';
import PackageIcon from '@mui/icons-material/Inventory';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import HistoryIcon from '@mui/icons-material/History';
import { Link } from 'react-router-dom';
import { getDashboardStats } from '../services/dashboardService';
import { useNavigation } from '../hooks/useNavigation';
import { useTranslation } from 'react-i18next';
import { InsightsPanel, AlertCenter } from '../components/insights';
import DashboardVmCard from '../components/dashboard/DashboardVmCard';
import DashboardCommonHome from '../components/dashboard/DashboardCommonHome';
import DashboardQuickActionsSection from '../components/dashboard/DashboardQuickActionsSection';
import { useCanViewDashboard } from '../hooks/useCanViewDashboard';

function HomePage() {
  const { getTenantPath } = useNavigation();
  const { t } = useTranslation();
  const { authenticated, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Dashboard (metrics + VM summary) vs. Painel (PBI #3968). Gated by
   * `dashboard:read` with admin bypass, the same pairing the routes and the
   * sidebar use — it used to be role-only, which no tenant could configure.
   */
  const canViewDashboard = useCanViewDashboard();

  const formatTimeAgo = useCallback((isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return t('dashboard.daysAgo', { count: diffDays });
    }
    if (diffHours > 0) {
      return t('dashboard.hoursAgo', { count: diffHours });
    }
    if (diffMins > 0) {
      return t('dashboard.minutesAgo', { count: diffMins });
    }

    return t('dashboard.justNow');
  }, [t]);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getDashboardStats();
      setStats({
        agents: data.agents,
        jobs: {
          ...data.jobs,
          success: data.jobs?.success || 0,
          failed: data.jobs?.failed || 0,
          running: data.jobs?.running || 0
        },
        automations: {
          total: data.jobs?.total || 0,
          active: data.jobs?.running || 0
        },
        schedules: data.schedules,
        packages: data.packages
      });

      const recentJobsData = data.recentJobs || data.recent_jobs || [];
      const transformedJobs = recentJobsData.map((job) => ({
        id: job.job_id || job.id,
        name: job.name,
        status: job.status,
        time: formatTimeAgo(job.started_at || job.time),
        successCount: job.success_count ?? job.successCount ?? 0,
        failedCount: job.failed_count ?? job.failedCount ?? 0
      }));
      setRecentJobs(transformedJobs);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);

      if (err.response?.status === 401) {
        console.log('Authentication required for dashboard data');
        return;
      }

      setError('loadError');
    } finally {
      setLoading(false);
    }
  }, [formatTimeAgo]);

  const tenantScopeKey =
    user?.tenant_id != null && user.tenant_id !== ''
      ? String(user.tenant_id)
      : user?.tenant_name || '';

  useEffect(() => {
    if (!authenticated) return undefined;
    if (!canViewDashboard) {
      setLoading(false);
      setStats(null);
      setError(null);
      return undefined;
    }
    fetchDashboardData();
    return undefined;
  }, [authenticated, tenantScopeKey, fetchDashboardData, canViewDashboard]);

  const getJobStatusLabel = (status) => {
    switch (status) {
      case 'completed':
      case 'success':
        return t('dashboard.status.completed');
      case 'failed':
        return t('dashboard.status.failed');
      case 'running':
        return t('dashboard.status.running');
      case 'pending':
        return t('dashboard.status.pending');
      default:
        return t('dashboard.status.pending');
    }
  };

  const getStatusChipClass = (status) => {
    if (status === 'completed' || status === 'success') {
      return 'alert-badge';
    }
    if (status === 'failed') {
      return 'alert-badge critical';
    }
    if (status === 'running') {
      return 'alert-badge warning';
    }
    return 'alert-badge';
  };

  // CA 1c / 3a of PBI #3968: without the permission there is no dashboard to
  // render and no error to show — the Painel *is* this profile's home page.
  if (!canViewDashboard) {
    return <DashboardCommonHome />;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {t(`dashboard.messages.${error}`)}
          <Button onClick={fetchDashboardData} sx={{ ml: 2 }}>
            {t('dashboard.messages.retry')}
          </Button>
        </Alert>
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box>
        <Alert severity="info">{t('dashboard.messages.noData')}</Alert>
      </Box>
    );
  }

  const successCount = stats.jobs?.success || 0;
  const failedCount = stats.jobs?.failed || 0;
  const executionTotal = successCount + failedCount;
  const successPct = executionTotal > 0 ? (successCount / executionTotal) * 100 : 0;
  const failedPct = executionTotal > 0 ? (failedCount / executionTotal) * 100 : 0;

  const statCards = [
    {
      label: t('dashboard.automations', 'Automações'),
      value: stats.automations?.total || 0,
      icon: <ElectricBoltIcon />,
      description: `${stats.automations?.active || 0} ${t('dashboard.automationsActive', 'ativas')}`,
      link: 'automations'
    },
    {
      label: t('dashboard.executors', 'Executores'),
      value: stats.agents.total,
      icon: <ComputerIcon />,
      description: `${stats.agents.online} ${t('dashboard.summary.executorsOnline')}`,
      link: 'executors'
    },
    {
      label: t('dashboard.executions', 'Execuções'),
      value: stats.jobs.total,
      icon: <TrendingUpIcon />,
      description: t('dashboard.executionSummary', {
        successful: successCount,
        failed: failedCount
      }),
      link: 'jobs'
    },
    {
      label: t('dashboard.packages', 'Pacotes'),
      value: stats.packages.total,
      icon: <PackageIcon />,
      description: t('dashboard.automationPackages', 'Pacotes de automação'),
      link: 'packages'
    }
  ];

  return (
    <div className="hub-dashboard-page section" data-testid="dashboard-admin-home">
      <DashboardQuickActionsSection user={user} />

      <div className="stats-cards">
        {statCards.map((stat) => (
          <Link
            key={stat.link}
            className="stat-card"
            to={getTenantPath(stat.link)}
          >
            <div className="stat-icon">{stat.icon}</div>
            <div className="stat-body">
              <div className="stat-value">{stat.value}</div>
              <div className="stat-label">{stat.label}</div>
              <div className="stat-sub">{stat.description}</div>
            </div>
          </Link>
        ))}
      </div>

      <div className="panel-row">
        <div className="panel panel-wide">
          <div className="panel-header">
            <h3>{t('dashboard.recentActivity', 'Atividade recente')}</h3>
            <Link to={getTenantPath('jobs')}>{t('dashboard.viewAll', 'Ver todos')}</Link>
          </div>

          {recentJobs.length > 0 ? (
            <div className="recent-list">
              {recentJobs.slice(0, 6).map((job) => (
                <Link
                  key={job.id}
                  className="recent-item"
                  to={getTenantPath(`jobs/${job.id}`)}
                >
                  <div className="stat-icon" aria-hidden="true">
                    <HistoryIcon />
                  </div>
                  <div className="recent-body">
                    <div className="recent-title">{job.name}</div>
                    <div className="recent-meta">
                      {t('dashboard.recentActivitySummary', {
                        successful: job.successCount || 0,
                        failed: job.failedCount || 0
                      })}
                    </div>
                  </div>
                  <span className={getStatusChipClass(job.status)}>
                    {getJobStatusLabel(job.status)}
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="panel-empty">{t('dashboard.noRecentActivity')}</div>
          )}
        </div>

        <div className="panel panel-mid">
          <div className="panel-header centered">
            <h3>{t('dashboard.executionStatistics', 'Estatísticas de execução')}</h3>
          </div>

          <div
            className="donut"
            style={{
              '--success-stop': `${successPct}%`,
              '--failed-stop': `${successPct + failedPct}%`
            }}
          >
            <div className="donut-inner">
              <div className="donut-value">{executionTotal}</div>
              <div className="donut-label">{t('dashboard.chart.total')}</div>
            </div>
          </div>

          <div className="mini-cards">
            <div className="mini-card" data-testid="mini-card-success">
              {successCount}
              <span>{t('dashboard.successful', 'Bem-sucedido')}</span>
            </div>
            <div className="mini-card danger">
              {failedCount}
              <span>{t('dashboard.recentJobs.failed')}</span>
            </div>
          </div>

          <div className="panel-footer">
            {t('dashboard.last30Days', 'Visão geral dos últimos 30 dias.')}
          </div>
        </div>

        <AlertCenter maxItems={10} panelLayout />
      </div>

      <div className="panel-row dashboard-vm-row" data-testid="dashboard-vm-row">
        <DashboardVmCard />
      </div>

      <InsightsPanel maxItems={5} autoRefresh />
    </div>
  );
}

export default HomePage;
